import React, {useEffect, useRef, useCallback} from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import './css/AppHeader.css';
import NotificationCenter from "@dashboard/notifications/NotificationCenter";
import {useStackedPopup} from "@dashboard/contexts/PopupStackContext";
import { useGlobalEvents } from "@dashboard/contexts/GlobalEventsContext";

const HEADER_TABS = [
    { id: 'MW',      label: 'MARKETWATCH',       componentKey: 'MW' },
    { id: 'ST',      label: 'STOCKTRACKING',     componentKey: 'ST' },
    { id: 'SM',      label: 'SHARPMONITORING',   componentKey: 'SM' },
    { id: 'SS',      label: 'SUPERSTARS',        componentKey: 'SS' },
    { id: 'SC',      label: 'STAGNENTCOMP',      componentKey: 'SC' },
    { id: 'PF',      label: 'PORTFOLIO',         componentKey: 'PF' },
    { id: 'ANN',     label: 'ANNOUNCEMENTS',     componentKey: 'ANN' },
    { id: 'RES',     label: 'RESULTDATES',       componentKey: 'RES' },
    { id: 'SR',      label: 'RESULTS',           componentKey: 'SR' },
    { id: 'POSGRID', label: 'POSITIONS',         componentKey: 'POS' },
    { id: 'ORD',     label: 'ORDERS',            componentKey: 'ORD' },
    { id: 'AO',      label: 'AdvancedOrders',    componentKey: 'AO' },
    { id: 'MON',     label: 'MONITORING',        componentKey: 'MON' },
    { id: 'PVA',     label: 'PRICEVOLUMEALERTS', componentKey: 'PVA' },
    { id: 'REM',     label: 'REMINDER',          componentKey: 'REM' },
    { id: 'UM',      label: 'USER MANAGEMENT',   componentKey: 'UM' },

];

// Sidebar should NOT show PVA as a section
const NAV_TABS = HEADER_TABS.filter(
    tab => tab.componentKey !== 'PVA' && tab.componentKey !== 'REM'
);

const AppHeader = ({
                       activeComponentKey,
                       onComponentSwitch,
                       onPendingContractNotesClick,
                       onManageAlertsClick,
                       onManageRemindersClick,
                   }) => {
    const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
    const [isAlertsMenuOpen, setIsAlertsMenuOpen] = React.useState(false);
    const actionsMenuRef = useRef(null);

    const { activeAlertTabs } = useGlobalEvents();

    const selectedTab =
        HEADER_TABS.find(tab => tab.componentKey === activeComponentKey) || HEADER_TABS[0];

    const handleSidebarClick = (tabId) => {
        const tab = NAV_TABS.find(t => t.id === tabId);
        if (!tab) return;
        onComponentSwitch(tab.componentKey);
        setIsSidebarOpen(false);
    };

    useEffect(() => {
        if (!isAlertsMenuOpen) return;

        const handleClickOutside = (event) => {
            if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
                setIsAlertsMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isAlertsMenuOpen]);

    const closeAlertsMenu = useCallback(() => {
        setIsAlertsMenuOpen(false);
    }, []);

    useStackedPopup(isAlertsMenuOpen, closeAlertsMenu);

    const openSidebar = () => {
        setIsSidebarOpen(true);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    const toggleAlertsMenu = () => {
        setIsAlertsMenuOpen(prev => !prev);
    };

    const handleManageAlerts = () => {
        closeAlertsMenu();
        if (onManageAlertsClick) {
            onManageAlertsClick();
        }
    };
    const handleManageReminders = () => {
        closeAlertsMenu();
        if (onManageRemindersClick) {
            onManageRemindersClick();
        }
    };

    const isStockTracking = selectedTab.componentKey === 'ST';

    const handleAlertTabClick = (componentKey) => {
        if (componentKey === 'PVA') {
            if (onManageAlertsClick) onManageAlertsClick();
            return;
        }
        if (componentKey === 'REM') {
            if (onManageRemindersClick) onManageRemindersClick();
            return;
        }
        onComponentSwitch(componentKey);
    };


    // Tabs that currently have alerts (according to GlobalEventsContext),
    // excluding the one that's already selected.
    const alertTabsToShow = HEADER_TABS.filter(
        (tab) =>
            tab.componentKey !== selectedTab.componentKey &&
            activeAlertTabs &&
            activeAlertTabs[tab.componentKey]
    );

    return (
        <>
            <AppBar position="static">
                <Toolbar className="app-header-toolbar"
                         sx={{
                             minHeight: 54,
                             px: 2,
                             "@media (min-width:600px)": { minHeight: 54 },
                         }}
                >
                    <div className="app-header-left">
                        <div className="app-header-title">
                            TERMINAL
                        </div>

                        <div className="app-header-current-tab">
                            <span className="app-header-current-tab-label">
                                {selectedTab.label}
                            </span>

                            {alertTabsToShow.length > 0 && (
                                <div className="app-header-alert-tabs">
                                    {alertTabsToShow.map((tab) => (
                                        <button
                                            key={tab.componentKey}
                                            className="app-header-alert-tab-badge"
                                            onClick={() => handleAlertTabClick(tab.componentKey)}
                                            title={
                                                tab.componentKey === 'PVA'
                                                    ? 'One or more Price / Volume Alerts have triggered'
                                                    : `${tab.label} has active alerts`
                                            }
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="app-header-right">
                        {isStockTracking && (
                            <div className="app-header-actions-wrapper">
                                <button
                                    className="app-header-actions-btn"
                                    onClick={toggleAlertsMenu}
                                >
                                    Actions ▾
                                </button>

                                {isAlertsMenuOpen && (
                                    <div ref={actionsMenuRef} className="app-header-actions-menu">
                                        <div
                                            className="app-header-actions-item"
                                            onClick={handleManageAlerts}
                                        >
                                            Manage PriceVolume alerts
                                        </div>
                                        <div
                                            className="app-header-actions-item"
                                            onClick={handleManageReminders}
                                        >
                                            Manage Reminders
                                        </div>
                                    </div>
                                )}

                            </div>
                        )}

                        <NotificationCenter
                            onPendingContractNotesClick={onPendingContractNotesClick}
                        />
                    </div>
                </Toolbar>
            </AppBar>

            <div
                className="header-sidebar-hover-zone"
                onMouseEnter={openSidebar}
            />

            <div
                className={`header-sidebar ${isSidebarOpen ? 'open' : ''}`}
                onMouseLeave={closeSidebar}
            >
                <div className="header-sidebar-inner">
                    <div className="header-sidebar-title">
                        SECTIONS
                    </div>

                    <div className="header-sidebar-list">
                        {NAV_TABS.map(tab => (
                            <button
                                key={tab.id}
                                className={
                                    'header-sidebar-item' +
                                    (tab.componentKey === activeComponentKey
                                        ? ' header-sidebar-item-active'
                                        : '')
                                }
                                onClick={() => handleSidebarClick(tab.id)}
                            >
                                <span className="header-sidebar-item-label">
                                    {tab.label}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AppHeader;
